import React, { useCallback, useEffect, useState } from 'react';
import { Container, Card, Button, Alert, Spinner, Icon } from '../../../../core/ui';
import { useNavigate } from 'react-router-dom';
import { listStatements, deleteStatement, listCards } from '../../api/budget';
import { useTranslation } from '../../../../core/context/TranslationContext';
import { formatTry } from '../../format';
import { formatShortDate, formatLongMonth, monthKey, dateSortValue } from '../../../../core/format/date';
import SectionedTable from '../../../../core/common/SectionedTable';
import ScopeSwitcher from '../../../family/components/ScopeSwitcher';
import styles from './StatementList.module.css';

const StatementList = () => {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const [statements, setStatements] = useState([]);
  const [cardsById, setCardsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [s, c] = await Promise.all([listStatements(), listCards()]);
      setStatements(s);
      const map = {};
      for (const card of c) map[card.id] = card;
      setCardsById(map);
    } catch (err) {
      setError(err?.message || t('error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id, e) => {
    e?.stopPropagation?.();
    if (!window.confirm(t('budget.confirmDeleteStatement'))) return;
    await deleteStatement(id);
    load();
  };

  const formatCardLabel = (cardId) => {
    const c = cardsById[cardId];
    if (!c) return `#${cardId}`;
    const parts = [c.bank, c.cardName, c.last4 ? `**${c.last4}` : null].filter(Boolean);
    return parts.join(' · ');
  };

  const columns = [
    {
      key: 'card',
      header: t('budget.card'),
      accessor: (r) => formatCardLabel(r.cardId),
      cell: (r) => <span className={styles.bold}>{formatCardLabel(r.cardId)}</span>,
    },
    {
      key: 'period',
      header: t('budget.period'),
      accessor: (r) => dateSortValue(r.periodStart),
      cell: (r) => (
        <span className={styles.periodCell}>
          {formatShortDate(r.periodStart, language)} → {formatShortDate(r.periodEnd, language)}
        </span>
      ),
      filterValue: (r) => `${formatShortDate(r.periodStart, language)} ${formatShortDate(r.periodEnd, language)}`,
    },
    {
      key: 'dueDate',
      header: t('budget.dueDate'),
      accessor: (r) => dateSortValue(r.dueDate),
      cell: (r) => (r.dueDate ? formatShortDate(r.dueDate, language) : '—'),
      filterValue: (r) => (r.dueDate ? formatShortDate(r.dueDate, language) : ''),
    },
    {
      key: 'totalDue',
      header: t('budget.total'),
      align: 'end',
      accessor: (r) => Number(r.totalDueTry || 0),
      cell: (r) => <span className={styles.amount}>{formatTry(r.totalDueTry)}</span>,
    },
    {
      key: 'minimumPayment',
      header: t('budget.minimumPayment'),
      align: 'end',
      accessor: (r) => Number(r.minimumPayment || 0),
      cell: (r) => (r.minimumPayment ? formatTry(r.minimumPayment) : '—'),
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      filterable: false,
      align: 'end',
      cell: (r) => (
        <div className={styles.btnGroup} onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="secondary" onClick={() => navigate(`/budget/statements/${r.id}`)}>
            {t('budget.viewDetails')}
          </Button>
          <Button size="sm" variant="dangerGhost" onClick={(e) => handleDelete(r.id, e)}>
            {t('delete')}
          </Button>
        </div>
      ),
    },
  ];

  const monthGroupBy = {
    key: (row) => monthKey(row.periodEnd) || '0000-00',
    label: (row) => formatLongMonth(row.periodEnd, language),
    order: (a, b) => b.localeCompare(a),
    trailing: (rows) => {
      const sum = rows.reduce((acc, r) => acc + Number(r.totalDueTry || 0), 0);
      return `${rows.length} • ${formatTry(sum)}`;
    },
  };

  return (
    <Container>
      <div className={styles.header}>
        <h2 className={styles.title}>{t('budget.statements')}</h2>
        <div className={styles.headerActions}>
          <ScopeSwitcher
            current="mine"
            mineRoute="/budget/statements"
            familyRoute="/family/statements"
          />
          <Button variant="primary" onClick={() => navigate('/budget/upload-statement')}>
            <Icon name="fa-upload" size="sm" className={styles.me2} />
            {t('budget.uploadStatement')}
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {loading && <Spinner />}

      {!loading && statements.length === 0 && (
        <Card>
          <Card.Body className={styles.centerMuted}>
            {t('budget.noStatementsYet')}
          </Card.Body>
        </Card>
      )}

      {!loading && statements.length > 0 && (
        <SectionedTable
          data={statements}
          columns={columns}
          rowKey={(r) => r.id}
          groupBy={monthGroupBy}
          initialSort={{ key: 'period', dir: 'desc' }}
          onRowClick={(r) => navigate(`/budget/statements/${r.id}`)}
          emptyMessage={t('budget.noStatementsYet')}
          enableColumnFilters
        />
      )}
    </Container>
  );
};

export default StatementList;
